import { useEffect, useReducer, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import { useBridgeApi } from '@/api/BridgeApi.ts';

import {
	derivePathFromState,
	diffRouteActions,
	parsePathname,
} from './urlHelpers.ts';

// How long the URL may describe a view the state hasn't reached yet. After a
// back/forward press we dispatch actions and wait for the server to echo the
// resulting state, but sendAction is fire-and-forget over a WebSocket — if the
// socket is down the action is silently dropped and no echo ever comes. Once
// this much time passes, stop waiting and move the URL back to match the state.
export const PENDING_ECHO_EXPIRY_MS = 3000;

// A back/forward press whose actions are still round-tripping to the server.
// Until the echo lands, the state still describes the page the user just left,
// so the state→URL direction must hold off — otherwise it would immediately
// shove the old path back into the address bar, fighting the user's press.
type PendingEcho = { path: string; until: number };

// /chat/<id> is just the fully-spelled form of / or /chat (all three mean "the
// chat view"); moving between them is a respelling, not a navigation.
const refinesToChatThread = (from: string, to: string): boolean =>
	(from === '/' || from === '/chat') && to.startsWith('/chat/');

/**
 * Keeps the address bar and the bridge's view state in sync, both directions.
 *
 * The visible view is fully described by user_state.current_space and
 * user_state.current_chat_id, both owned by the server: the client requests
 * changes via actions and renders whatever state comes back. This hook is the
 * only place the URL exists — no view reads or writes it. State changes are
 * mirrored into the URL as history entries (so back/forward have somewhere to
 * go), and URL changes the app didn't itself make — a cold load on a deep
 * link, or a back/forward press — are converged by dispatching the difference
 * as bridge actions.
 *
 * user_state is mutated in place when server deltas arrive (the object's
 * identity never changes), so everything here keys off derived strings, never
 * off the state object itself.
 */
export const useUrlSync = (): void => {
	const bridgeApi = useBridgeApi();
	const location = useLocation();
	const navType = useNavigationType();
	const navigate = useNavigate();

	const didInitRef = useRef(false);
	// location.key of the last location this hook processed. A POP with a new
	// key is a real back/forward press; re-renders while the echo is in flight
	// keep the same key and must not re-dispatch (the actions already went out).
	const lastKeyRef = useRef<string | null>(null);
	// Browser history position (window.history.state.idx, maintained by the
	// history package under BrowserRouter) of the last location this hook
	// processed. location.key alone can't tell a real back/forward press from a
	// re-render when a push and its undoing POP coalesce in one tick — the push's
	// effect never commits its key, so the POP lands on a key that still looks
	// current. The position moves even then, so it's the reliable signal. Null
	// under MemoryRouter (tests), where the key comparison below takes over.
	const lastIdxRef = useRef<number | null>(null);
	// derivePathFromState value from the last render this hook processed. A POP
	// whose history position moved is normally a back/forward press, but a rapid
	// in-app chat switch can collapse its state→URL push into a same-tick POP:
	// the state has already advanced to the clicked thread while the address bar
	// still trails on the one we came from. In that render the derived path
	// differs from this ref (the state moved this render), which is exactly what
	// separates it from a genuine back/forward, where the state held still and
	// only the URL moved. Stays inert under MemoryRouter, where the position
	// index is unknown and traversal detection falls back to the key comparison.
	const lastDerivedPathRef = useRef<string | null>(null);
	const pendingRef = useRef<PendingEcho | null>(null);
	// Path whose pending echo expired without arriving. Re-converging away from
	// it replaces instead of pushes: the state refused this URL, so it shouldn't
	// survive as a history entry the back button would land on.
	const expiredPathRef = useRef<string | null>(null);
	// The last navigation issued from the current history entry. The router may
	// not have applied it yet when this effect runs again (StrictMode re-invokes
	// mount effects; location updates can be transition-deferred), and issuing
	// the same navigation twice would push a duplicate history entry.
	const lastNavRef = useRef<{ fromKey: string; to: string } | null>(null);
	const [expiryTick, armExpiryTick] = useReducer((t: number) => t + 1, 0);

	const derivedPath = derivePathFromState(bridgeApi.user_state);

	// The history package stores the current entry's position index on
	// window.history.state.idx under BrowserRouter. Returns null when there's no
	// window, no history state, or the idx isn't a number — i.e. under
	// MemoryRouter — which routes traversal detection back to the key comparison.
	const readHistoryIdx = (): number | null => {
		if (typeof window === 'undefined') return null;
		const idx = (window.history.state as { idx?: unknown } | null)?.idx;
		return typeof idx === 'number' ? idx : null;
	};

	useEffect(() => {
		// expiryTick exists only to re-run this effect when the pending-echo
		// timer (armed at the bottom) fires; nothing reads its value.
		void expiryTick;

		const userState = bridgeApi.user_state;
		if (derivedPath === null || userState === null) {
			return; // no server state yet — nothing to sync
		}

		// Detect a back/forward press by history position, falling back to
		// location.key when the position is unknown (MemoryRouter, in tests).
		//
		// Why position and not just key: opening a thread pushes a history entry
		// via navigateTo below; pressing Back in the same render tick coalesces
		// that push with the POP, so the pushed entry's effect never runs and
		// lastKeyRef is never advanced to it. The POP then lands on a key that
		// still equals lastKeyRef, so a key-only check reads it as "unchanged"
		// and skips convergence — the URL and state stay stuck on different
		// threads. The position index moves even in that coalesced case (the
		// navigateTo linchpin below captures it synchronously), so it catches the
		// press the key comparison misses.
		//
		//   open thread B, then Back, same tick:
		//     navigateTo(/chat/B) pushes: idx A -> B, lastIdxRef captured = B
		//     /chat/B effect never commits -> lastKeyRef still A
		//     POP arrives: key == A (looks unchanged), idx == A != lastIdxRef(B)
		//     key-only:  not new -> convergence skipped        (stuck) BUG
		//     position:  idx moved -> traversal -> do_switch_chat back to A  FIX
		const currentIdx = readHistoryIdx();
		const idxKnown = currentIdx !== null && lastIdxRef.current !== null;
		const movedPosition = idxKnown
			? currentIdx !== lastIdxRef.current
			: location.key !== lastKeyRef.current;
		const isFirstRun = !didInitRef.current;
		const isNewLocation = !isFirstRun && movedPosition;
		// A POP whose position moved but whose derived path also changed this
		// render is not a back/forward press — it's a rapid in-app switch whose
		// state→URL push collapsed into a same-tick POP. The clicked thread is
		// already the current state; the address bar is merely a step behind. On a
		// genuine back/forward the state held still and only the URL moved, so its
		// derived path matches what we saw last render. Only meaningful when the
		// position index is known (BrowserRouter); under MemoryRouter the key
		// fallback governs and this stays false, preserving today's behavior.
		const stateAdvancedInApp =
			idxKnown && derivedPath !== lastDerivedPathRef.current;
		// useNavigationType() reports POP for the initial location too, so POP
		// only means back/forward when the location actually changed — and never
		// when the state, not the URL, is the one that just moved.
		const isTraversal =
			isNewLocation && navType === 'POP' && !stateAdvancedInApp;
		didInitRef.current = true;
		lastKeyRef.current = location.key;
		lastIdxRef.current = currentIdx;
		lastDerivedPathRef.current = derivedPath;
		if (isNewLocation) {
			lastNavRef.current = null;
			expiredPathRef.current = null;
		}

		const navigateTo = (to: string, replace: boolean): void => {
			if (
				lastNavRef.current?.fromKey === location.key &&
				lastNavRef.current?.to === to
			) {
				return;
			}
			lastNavRef.current = { fromKey: location.key, to };
			navigate(to, { replace });
			// navigate() runs window.history.pushState synchronously and the
			// history package writes the new position index into
			// window.history.state during that call, so the pushed entry's index
			// is readable right now. Capturing it here (past the dedupe return, so
			// a suppressed duplicate nav doesn't move it) means a push that
			// coalesces with a same-tick Back still advances lastIdxRef — the
			// pushed entry's own effect may never run to do it.
			lastIdxRef.current = readHistoryIdx();
		};

		if (pendingRef.current && pendingRef.current.until <= Date.now()) {
			// The echo never came (likely a dropped action on a closed socket).
			// Let the state win again; remember the path so the re-convergence
			// below replaces it rather than stacking a new entry on top of it.
			expiredPathRef.current = pendingRef.current.path;
			pendingRef.current = null;
		}

		if (pendingRef.current) {
			// A traversal settles when the state reaches the URL being converged
			// on — or when the only gap left is the chat id, which the pending
			// URL doesn't spell but the derived one does. In that case the URL
			// just needs its respelling, no further state movement is coming.
			const settled =
				derivedPath === pendingRef.current.path ||
				refinesToChatThread(pendingRef.current.path, derivedPath);
			if (settled) {
				pendingRef.current = null;
			}
		}

		if (derivedPath === location.pathname) {
			pendingRef.current = null;
			return;
		}

		// Dispatch whatever actions this URL needs the state to catch up on.
		// Returns false when the state already matches the URL as closely as it
		// ever will, i.e. the remaining difference is the URL's to fix.
		const convergeStateToUrl = (isInitial: boolean): boolean => {
			const actions = diffRouteActions(
				parsePathname(location.pathname),
				userState,
				{ isInitial },
			);
			for (const action of actions) {
				bridgeApi.sendAction(action);
			}
			if (actions.length > 0) {
				pendingRef.current = {
					path: location.pathname,
					until: Date.now() + PENDING_ECHO_EXPIRY_MS,
				};
			}
			return actions.length > 0;
		};

		if (isTraversal) {
			// Back/forward: the URL is the user's intent; make the state follow.
			if (!convergeStateToUrl(false)) {
				navigateTo(derivedPath, true);
			}
		} else if (isFirstRun) {
			// Cold load: honor the URL the user arrived on (deep link). When it
			// asks for nothing the state doesn't already have, normalize the URL
			// to the state's path without creating a history entry — this is how
			// a bogus path, a bare /chat, or a / with a server-persisted space
			// settles.
			if (!convergeStateToUrl(true)) {
				navigateTo(derivedPath, true);
			}
		} else if (pendingRef.current?.path !== location.pathname) {
			// The state changed in-app (nav click, server-side navigation, another
			// tab): mirror it into the URL. Push, so back/forward have somewhere
			// to go — except when the move only fills in the chat id over / or
			// /chat (the auto-created chat right after load, or the tail end of a
			// back press to /): pushing there would leave a / entry that the next
			// back press bounces straight off of. A path whose pending echo
			// expired is replaced too, so a URL the state refused doesn't linger
			// in history.
			navigateTo(
				derivedPath,
				refinesToChatThread(location.pathname, derivedPath) ||
					expiredPathRef.current === location.pathname,
			);
		}
		// (else: a traversal's echo is still in flight for this URL — leave the
		// URL alone; the settle rules or the expiry timer below will resolve it.)

		if (pendingRef.current) {
			const delay = Math.max(0, pendingRef.current.until - Date.now());
			const timer = setTimeout(armExpiryTick, delay);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [bridgeApi, derivedPath, expiryTick, location, navType, navigate]);
};
